import {
  Zap, BarChart3, Boxes, Users, IndianRupee,
  Receipt, UserCircle2, LineChart, CreditCard,
} from "lucide-react";
import { Button } from "../ui/button";
import Reveal from "../Reveal";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 lg:pt-36 lg:pb-24 bg-gradient-to-b from-blue-50/60 via-white to-white"
    >
      {/* soft background tints */}
      <div className="absolute top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-blue-100/60 blur-3xl pointer-events-none" />
      <div className="absolute -top-10 right-0 h-96 w-96 rounded-full bg-navy/[0.05] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6">
            <Reveal>
              <span className="inline-flex items-center rounded-full bg-blue-100 text-navy px-4 py-1.5 text-[12px] font-bold tracking-wide">
                Our Main Product
              </span>
            </Reveal>

            <Reveal delay={80}>
              <h1
                data-testid="hero-headline"
                className="font-display font-black text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-navy mt-6"
              >
                Smart Billing.<br />
                Simplified Business.<br />
                <span className="text-blue-800">FolioStax<span className="text-navy">.</span></span>
              </h1>
            </Reveal>

            <Reveal delay={140}>
              <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                FolioStax is a powerful and easy-to-use billing app designed for Cafes, Grocery
                Stores, Supermarkets, Retail Shops and more — by{" "}
                <span className="font-semibold text-navy">Fyzeron Technologies LLP</span>.
              </p>
            </Reveal>

            {/* Quick feature pills */}
            <Reveal delay={200}>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: Zap, t: "Fast Billing",     s: "Bill in seconds",  color: "from-blue-100 to-blue-50", iconColor: "text-blue-700" },
                  { icon: BarChart3, t: "Business Insights", s: "Track sales & profit", color: "from-emerald-100 to-emerald-50", iconColor: "text-emerald-600" },
                  { icon: Boxes, t: "Inventory Control",     s: "Manage stock easily", color: "from-violet-100 to-violet-50", iconColor: "text-violet-600" },
                  { icon: Users, t: "Multiple Users",        s: "Work as a team",      color: "from-amber-100 to-amber-50",  iconColor: "text-amber-600"  },
                ].map((p) => (
                  <div key={p.t} className="flex items-start gap-3">
                    <span className={`h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br ${p.color} grid place-items-center`}>
                      <p.icon className={`h-5 w-5 ${p.iconColor}`} />
                    </span>
                    <div>
                      <p className="font-display font-bold text-navy text-sm leading-tight">{p.t}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{p.s}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Store badges */}
            <Reveal delay={280}>
              <div id="download" className="mt-10 flex flex-wrap items-center gap-3">
                <a
                  href="#contact"
                  data-testid="hero-google-play"
                  className="flex items-center gap-3 rounded-xl bg-navy hover:bg-navy/90 text-white pl-4 pr-5 h-14 transition shadow-md shadow-navy/20"
                >
                  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor"><path d="M3.6 2.3c-.4.2-.6.6-.6 1.2v17c0 .6.2 1 .6 1.2l.1.1L13 12.1v-.2L3.7 2.3h-.1zm10.6 10.1l3.1 3.1-9.9 5.6c-.5.3-.9.3-1.3.1l8.1-8.8zm0-.6L6.1 2.9c.4-.2.8-.2 1.3.1l9.9 5.6-3.1 3.2zm4.8 4.2L15.7 13l3.3-3.3 2.4 1.4c.8.5.8 1.6 0 2.1l-2.4 1.4-.2.1z"/></svg>
                  <span className="leading-tight text-left">
                    <span className="block text-[10px] uppercase tracking-wider opacity-80">Coming soon on</span>
                    <span className="block font-display font-bold text-base">Google Play</span>
                  </span>
                </a>
              </div>
            </Reveal>
          </div>

          {/* CENTER — phone mockups */}
          <div className="lg:col-span-5 lg:col-start-7 relative">
            <Reveal delay={120}>
              <PhonesMockup />
            </Reveal>
          </div>

          {/* RIGHT side icons (hidden on mobile, absolute right on lg) */}
          <div className="hidden xl:flex lg:col-span-1 absolute right-2 top-1/2 -translate-y-1/2 flex-col gap-7">
            {[
              { icon: IndianRupee, t: "Quick Billing" },
              { icon: Receipt,     t: "Inventory" },
              { icon: UserCircle2, t: "Customer" },
              { icon: LineChart,   t: "Reports" },
              { icon: CreditCard,  t: "Payments" },
            ].map((it, i) => (
              <Reveal key={it.t} delay={i * 80} className="flex flex-col items-center">
                <span className="h-12 w-12 rounded-2xl bg-white border border-slate-200 shadow-md grid place-items-center">
                  <it.icon className="h-5 w-5 text-blue-800" />
                </span>
                <p className="mt-2 text-[11px] font-semibold text-slate-700 text-center leading-tight max-w-[78px]">
                  {it.t}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneFrame({ children, rotate = 0 }) {
  return (
    <div
      style={{ transform: `rotate(${rotate}deg)` }}
      className="relative w-[230px] sm:w-[260px] rounded-[36px] bg-navy p-2 shadow-2xl"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-5 w-24 bg-navy rounded-b-2xl z-10" />
      <div className="rounded-[30px] overflow-hidden bg-white">{children}</div>
    </div>
  );
}

function PhonesMockup() {
  return (
    <div className="relative h-[560px] lg:h-[600px]">
      {/* Phone 1 — Dashboard */}
      <div className="absolute left-0 top-6 -rotate-[6deg]">
        <PhoneFrame>
          <div className="bg-navy text-white px-4 pt-6 pb-3">
            <div className="flex items-center justify-between text-[10px] opacity-80">
              <span>9:41</span>
              <span>● ●</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-1.5">
                <span className="text-base">≡</span>
                <span className="font-display font-bold text-base">FolioStax</span>
              </div>
              <span className="text-base">🔔</span>
            </div>
          </div>
          <div className="p-3 space-y-3 bg-slate-50">
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-slate-500">Total Sales</span>
                <span className="text-[10px] text-slate-400">Today ▾</span>
              </div>
              <p className="font-display font-black text-navy text-xl mt-1">₹ 18,750.00</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { l: "Invoices",   v: "48",   c: "bg-blue-50 text-blue-800" },
                { l: "Receivables",v: "₹6,250", c: "bg-emerald-50 text-emerald-700" },
                { l: "Customers",  v: "120",  c: "bg-violet-50 text-violet-700" },
                { l: "Products",   v: "320",  c: "bg-amber-50 text-amber-700" },
              ].map((s) => (
                <div key={s.l} className="rounded-lg bg-white border border-slate-200 p-2">
                  <p className="text-[9px] text-slate-500">{s.l}</p>
                  <p className={`font-display font-bold text-sm mt-0.5 ${s.c.split(" ")[1]}`}>{s.v}</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                <span>Sales Overview</span><span>This Week</span>
              </div>
              <svg viewBox="0 0 200 80" className="w-full h-16">
                <polyline points="0,60 30,40 60,55 90,30 120,42 150,15 180,28 200,20" fill="none" stroke="#1E3A8A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                {[[0,60],[30,40],[60,55],[90,30],[120,42],[150,15],[180,28]].map(([x,y],i)=> (
                  <circle key={i} cx={x} cy={y} r="3" fill="#1E3A8A" />
                ))}
              </svg>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-700 mb-1">Recent Invoices</p>
              <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-100">
                {[
                  { n: "INV-10048", c: "Walk-in", a: "₹1,250", t: "Today" },
                  { n: "INV-10047", c: "Rahul S.", a: "₹3,450", t: "Today" },
                ].map((r) => (
                  <div key={r.n} className="flex items-center justify-between px-2 py-2">
                    <div>
                      <p className="text-[10px] font-semibold text-navy">{r.n}</p>
                      <p className="text-[9px] text-slate-500">{r.c}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-emerald-600">{r.a}</p>
                      <p className="text-[9px] text-slate-400">{r.t}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </PhoneFrame>
      </div>

      {/* Phone 2 — Create Invoice */}
      <div className="absolute right-0 top-0 rotate-[5deg]">
        <PhoneFrame>
          <div className="bg-navy text-white px-4 pt-6 pb-3">
            <div className="flex items-center justify-between text-[10px] opacity-80">
              <span>9:41</span>
              <span>● ●</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-base">←</span>
              <span className="font-display font-bold text-base">Create Invoice</span>
            </div>
          </div>
          <div className="p-3 space-y-2 bg-slate-50">
            <div className="rounded-xl bg-white border border-slate-200 p-2 flex justify-between items-center">
              <div>
                <p className="text-[9px] text-slate-500">Walk-in Customer</p>
              </div>
              <span className="text-[10px] font-bold text-blue-800">+ Add Item</span>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-100">
              {[
                { e: "☕", n: "Cappuccino",   q: "1 × ₹120.00", a: "₹120.00" },
                { e: "🥪", n: "Sandwich",     q: "2 × ₹150.00", a: "₹300.00" },
                { e: "🧋", n: "Cold Coffee",  q: "1 × ₹130.00", a: "₹130.00" },
                { e: "🍰", n: "Chocolate Cake", q: "1 × ₹110.00", a: "₹110.00" },
              ].map((r) => (
                <div key={r.n} className="flex items-center gap-2 px-2 py-2">
                  <span className="h-8 w-8 rounded-lg bg-blue-50 grid place-items-center text-sm">{r.e}</span>
                  <div className="flex-1">
                    <p className="text-[10px] font-semibold text-navy">{r.n}</p>
                    <p className="text-[9px] text-slate-500">{r.q}</p>
                  </div>
                  <p className="text-[10px] font-bold text-navy">{r.a}</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-2 text-[10px] space-y-1">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>₹660.00</span></div>
              <div className="flex justify-between text-slate-600"><span>Discount</span><span>- ₹10.00</span></div>
              <div className="flex justify-between text-slate-600"><span>Tax (5%)</span><span>₹32.50</span></div>
              <div className="flex justify-between font-bold text-navy pt-1 border-t border-slate-100"><span>Total</span><span>₹682.50</span></div>
            </div>
            <button className="w-full rounded-xl bg-blue-800 hover:bg-blue-900 text-white text-[11px] font-bold py-2.5">Continue to Pay</button>
            <button className="w-full rounded-xl border border-slate-200 text-[11px] font-semibold text-slate-700 py-2">Save as Draft</button>
          </div>
        </PhoneFrame>
      </div>
    </div>
  );
}
